import { useEffect, useMemo, useRef, useState, KeyboardEvent } from 'react';
import { WidgetShell } from '@ai-chatbot/ui-kit';
import { ChatClient } from '@ai-chatbot/client-sdk';

type Source = {
  title: string;
  url?: string;
};

type Message = {
  id: string;
  sender: 'user' | 'bot';
  text: string;
  sources?: Source[];
};

const initialMessages: Message[] = [
  { id: 'welcome-bot', sender: 'bot', text: '社内向けAIチャットボットです。何をお手伝いできますか？' }
];

const App = () => {
  const [isOpen, setIsOpen] = useState(false); // Widget open/close state
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [draft, setDraft] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null); // null: 検証中, true: 認証済み, false: 未認証
  const [isVerifying, setIsVerifying] = useState(true); // 認証検証中
  const messagesRef = useRef<HTMLDivElement | null>(null);

  // Configuration from window object (injected by host) or env vars
  const config = window.chatbotConfig;
  
  const apiBaseUrl = config?.apiBaseUrl ?? import.meta.env.VITE_WIDGET_API_BASE_URL ?? '/api';
  
  // 認証が必要かどうか（環境変数で制御）
  const requireAuth = import.meta.env.VITE_REQUIRE_AUTH === 'true';
  
  const widgetHeaders = useMemo(() => {
    const headers: Record<string, string> = {
      'X-User-Id': config?.userId ?? import.meta.env.VITE_WIDGET_USER_ID ?? 'widget-user',
      'X-User-Role': import.meta.env.VITE_WIDGET_USER_ROLE ?? 'user'
    };
    
    // Inject Authorization header if token exists
    if (config?.token) {
      headers['Authorization'] = `Bearer ${config.token}`;
    }
    
    return headers;
  }, [config]);

  const chatClient = useMemo(
    () =>
      new ChatClient({
        baseUrl: apiBaseUrl,
        headers: widgetHeaders
      }),
    [apiBaseUrl, widgetHeaders]
  );

  const isSendDisabled = useMemo(() => !draft.trim() || isSending, [draft, isSending]);

  // 認証検証（JWTトークンがある場合のみ）
  useEffect(() => {
    const verifyAuth = async () => {
      // 認証が不要な場合はスキップ
      if (!requireAuth) {
        setIsAuthenticated(true);
        setIsVerifying(false);
        return;
      }

      // トークンがない場合は認証失敗
      if (!config?.token) {
        console.warn('Widget: No token provided, authentication required');
        setIsAuthenticated(false);
        setIsVerifying(false);
        return;
      }

      try {
        // トークンの有効性を検証
        const response = await fetch(`${apiBaseUrl}/auth/verify`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${config.token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.valid) {
            setIsAuthenticated(true);
          } else {
            console.warn('Widget: Token verification failed', data);
            setIsAuthenticated(false);
          }
        } else {
          console.warn('Widget: Token verification failed', { status: response.status });
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Widget: Token verification error', error);
        setIsAuthenticated(false);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyAuth();
  }, [requireAuth, config?.token, apiBaseUrl]);

  useEffect(() => {
    if (isOpen && messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages, isSending, isOpen]); // Scroll on new messages or typing indicator

  const handleSend = async () => {
    if (!draft.trim()) return;
    const userMessage: Message = {
      id: crypto.randomUUID(),
      sender: 'user',
      text: draft.trim()
    };
    setMessages((prev) => [...prev, userMessage]);
    setDraft('');
    setIsSending(true);
    try {
      const response = await chatClient.sendMessage({ message: userMessage.text });
      
      // 安全にreplyを取得（オブジェクトの場合は文字列化）
      let replyText: string;
      if (typeof response.reply === 'string') {
        replyText = response.reply;
      } else if (response.reply && typeof response.reply === 'object' && '$$typeof' in response.reply) {
        // React要素の場合は警告を出して空文字列にする
        console.error('⚠️ React element detected in response.reply!', response.reply);
        replyText = '応答を取得できませんでした';
      } else {
        replyText = String(response.reply ?? '応答を取得できませんでした');
      }
      
      // 安全にsourcesを処理
      const sources: Source[] = Array.isArray(response.sources) 
        ? response.sources
            .map((source: unknown, index: number): Source | null => {
              if (source && typeof source === 'object' && 'title' in source) {
                // titleがReact要素の場合は警告を出して空文字列にする
                let title: string;
                if (typeof source.title === 'string') {
                  title = source.title;
                } else if (source.title && typeof source.title === 'object' && '$$typeof' in source.title) {
                  console.error(`⚠️ React element detected in source[${index}].title!`, source.title);
                  title = '';
                } else {
                  title = String(source.title ?? '');
                }
                
                const url = 'url' in source && typeof source.url === 'string' ? source.url : undefined;
                return {
                  title,
                  ...(url ? { url } : {})
                };
              }
              console.warn(`Invalid source[${index}]:`, source);
              return null;
            })
            .filter((s): s is Source => s !== null)
        : [];
      
      const uniqueSources = sources.reduce<Source[]>((acc: Source[], current: Source) => {
        const isDuplicate = acc.some(
          (item: Source) => 
            (item.url && item.url === current.url) || 
            (item.title === current.title)
        );
        if (!isDuplicate) {
          acc.push(current);
        }
        return acc;
      }, []);

      const botMessage: Message = {
        id: crypto.randomUUID(),
        sender: 'bot',
        text: replyText,
        sources: uniqueSources
      };
      
      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error('Chat API Error:', error);
      if (error instanceof Error) {
        console.error('Error details:', { message: error.message, stack: error.stack });
      }
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          sender: 'bot',
          text: 'すみません、応答に失敗しました。時間をおいて再度お試しください。'
        }
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isSendDisabled) {
      e.preventDefault();
      handleSend();
    }
  };


  // 認証が必要な場合のチェック
  if (requireAuth) {
    // 認証検証中は何も表示しない
    if (isVerifying) {
      return null;
    }
    
    // 認証に失敗した場合は何も表示しない（ウィジェットを非表示）
    if (isAuthenticated === false) {
      console.warn('Widget: Authentication failed, widget will not be displayed');
      return null;
    }
  }
  
  if (!isOpen) {
    return (
      <div className="widget-launcher" onClick={() => setIsOpen(true)}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
      </div>
    );
  }

  return (
    <div className="widget-root">
      <WidgetShell 
        className="widget-shell" 
        title="AI チャット" 
        onClose={() => setIsOpen(false)}
      >
        <section className="widget-messages" ref={messagesRef}>
          {messages.map((message) => {
            // React要素（JSX要素）を検出して文字列に変換
            let messageText: string;
            if (typeof message.text === 'string') {
              messageText = message.text;
            } else if (message.text && typeof message.text === 'object' && '$$typeof' in message.text) {
              // React要素の場合は警告を出して空文字列にする
              console.error('⚠️ React element detected in message.text!', message.text);
              messageText = '';
            } else {
              messageText = String(message.text ?? '');
            }
            
            // 念のため、レンダリング直前に再度チェック
            const safeMessageText = typeof messageText === 'string' 
              ? messageText 
              : (messageText && typeof messageText === 'object' && '$$typeof' in messageText 
                  ? (console.error('⚠️ React element detected in messageText at render time!', messageText), '')
                  : String(messageText ?? ''));
            
            const bubbleClassName = `bubble bubble-${message.sender}`;
            
            return (
              <div key={message.id} className={bubbleClassName}>
                <div className="bubble-text">{safeMessageText}</div>
                {Array.isArray(message.sources) && message.sources.length > 0 && (
                  <div className="bubble-sources">
                    <div className="sources-title">参考資料:</div>
                    <ul className="sources-list">
                      {message.sources.map((source, idx) => {
                        // React要素（JSX要素）を検出して文字列に変換
                        let sourceTitle: string;
                        if (typeof source.title === 'string') {
                          sourceTitle = source.title;
                        } else if (source.title && typeof source.title === 'object' && '$$typeof' in source.title) {
                          // React要素の場合は警告を出して空文字列にする
                          console.error(`⚠️ React element detected in source[${idx}].title!`, source.title);
                          sourceTitle = '';
                        } else {
                          sourceTitle = String(source.title ?? '');
                        }
                        
                        const sourceUrl = source.url && typeof source.url === 'string' ? source.url : undefined;
                        
                        return (
                          <li key={idx}>
                            {sourceUrl ? (
                              <a href={sourceUrl} target="_blank" rel="noopener noreferrer">
                                {sourceTitle}
                              </a>
                            ) : (
                              <span>{sourceTitle}</span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
          
          {isSending && (
            <div className="bubble bubble-typing">
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
            </div>
          )}
        </section>
        <footer className="widget-input">
          <input
            type="text"
            value={draft}
            placeholder="質問を入力..."
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button onClick={handleSend} disabled={isSendDisabled}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          </button>
        </footer>
      </WidgetShell>
    </div>
  );
};

export default App;
